import type { ViewError } from '~/features/subscriber/sign-in'

import { api } from '@setplex/tria-api'
import { createEffect, createEvent, createStore, sample } from 'effector'
import { condition, delay, not } from 'patronum'
import { debounce } from 'patronum/debounce'
import { t } from '~/core/i18n'
import { model as router } from '~/core/router'
import { AuthTab, model as tabsModel } from '~/features/change-auth-type'
import {
  signInByEmailModel,
  signInByPhoneModel,
  signInByTrueIdFeatureModel,
  model as signInModel,
} from '~/features/subscriber/sign-in'
import { token } from '~/routes/auth/shared/tools'
import { lookbehind } from '~/shared/effector'
import { aye } from '~/shared/helpers'
import { formatDateDuration } from '~/shared/helpers/date'
import { actualUserInput } from '~/shared/html-events/input'
import { logger } from '~/shared/logger'
import { stopTimerError, updateTextError } from '../shared/model'

// init page
export const init = createEvent()
export const left = createEvent()

// effect to create hash token
const tokenFx = createEffect<
  { id: string; time: number; unit: string },
  string
>(({ id, time, unit }) => token(id, time, unit))

// in case we could not encode token -> log error
sample({
  clock: tokenFx.fail,
  fn: (fail) => ['could not encode token:', fail],
  target: logger.errorFx,
})

// redirect to verify page
sample({
  clock: tokenFx.doneData,
  source: signInByPhoneModel.signInByPhoneFx.done,
  fn: ({ params }, token) => ({
    pathname: `/sign-in/verify?=${AuthTab.Phone}`,
    hash: token,
    state: params,
  }),
  target: router.navigatePush,
})

// error to show to the user
export const $error = createStore<ViewError>(null)
export const onError = createEvent<ViewError | null>()

// send sign-in request
sample({
  clock: signInByEmailModel.signInForm.formValidated,
  source: { rememberMe: signInModel.$rememberMe },
  fn: ({ rememberMe }, { username, password }) => ({
    username: username.trim(),
    password,
    rememberMe,
  }),
  target: signInModel.signIn,
})

sample({
  clock: signInByPhoneModel.signInByPhoneFx.doneData,
  fn: ({ id, time_value, time_period }) => ({
    id,
    time: time_value,
    unit: time_period,
  }),
  target: tokenFx,
})

sample({
  clock: init,
  target: [
    signInByEmailModel.signInForm.reset,
    signInByPhoneModel.signInByPhoneForm.reset,
  ],
})

const userUpdatesForm = actualUserInput(
  signInByEmailModel.signInForm.$values.updates
)

// Grace period after init: ignore userUpdatesForm from form reset to prevent
// ErrorMessage flicker when login data is auto-filled by the browser
const INIT_GRACE_MS = 250
const initGraceEnd = delay(init, INIT_GRACE_MS)
const $initGracePeriod = createStore(false)
  .on(init, () => true)
  .on(initGraceEnd, () => false)
const userUpdatesFormAfterGrace = createEvent()
condition({
  source: userUpdatesForm,
  if: not($initGracePeriod),
  then: userUpdatesFormAfterGrace,
})

//
// handle dynamic fields errors (~ server side validation)
//

// WG0022 - Email is not unique
sample({
  clock: api.events.logic.emailNotUnique,
  fn: () => ({ rule: 'unique' }),
  target: signInByEmailModel.signInForm.fields.username.addError,
})

//
// add listeners for server errors
//

const initNot401 = lookbehind({
  source: init,
  clear: api.events.http.unauthorized,
  within: 100,
})

// signInModel.showSubscriptionErrorMessage event is triggered after
// api.events.logic.subscriptionHasBeenExpired / api.events.logic.accountOrDeviceHasBeenSuspended / api.events.logic.subscriptionIsApprovalPending
// only if there is payload with announcement message in 401 response - in that case we do not show error message,
// because this event redirects to /subscription-status page with its own message about expired subscription.
// BUT if there is no payload with announcement message in 401 response - in that case we show error message about expired subscription here.
// and to distinguish these two cases we use lookbehind to know if
// api.events.logic.subscriptionHasBeenExpired / api.events.logic.accountOrDeviceHasBeenSuspended / api.events.logic.subscriptionIsApprovalPending
// was triggered, but signInModel.showSubscriptionErrorMessage was not triggered.

const artificialSubscriptionHasBeenExpired = lookbehind({
  source: delay(api.events.logic.subscriptionHasBeenExpired, 100),
  clear: signInModel.showSubscriptionErrorMessage,
  within: 100,
})

const artificialAccountOrDeviceHasBeenSuspended = lookbehind({
  source: delay(api.events.logic.accountOrDeviceHasBeenSuspended, 100),
  clear: signInModel.showSubscriptionErrorMessage,
  within: 100,
})

const artificialSubscriptionIsApprovalPending = lookbehind({
  source: delay(api.events.logic.subscriptionIsApprovalPending, 100),
  clear: signInModel.showSubscriptionErrorMessage,
  within: 100,
})

//

$error
  .reset(
    userUpdatesFormAfterGrace,
    initNot401,
    signInModel.signIn,

    // "server validation" errors
    api.events.logic.emailNotUnique,
    api.events.logic.accountIpACLLock, // handled by geoblock navigation sub-process

    // do not show error on special error cases,
    // because they are handled by separate logic and have their own error message
    api.events.logic.subscriptionHasBeenExpired,
    api.events.logic.accountOrDeviceHasBeenSuspended,
    api.events.logic.subscriptionIsApprovalPending
  )
  .on(
    api.events.logic.apiBlocked, // EC300 | WG0019
    () => 'provider_is_blocked_error'
  )
  .on(
    api.events.logic.authNotAllowedIPAddress, // EC50 | WG0017
    () => 'ip_blocked_error'
  )
  .on(
    api.events.logic.badCredentials, // EC10 | WG0008
    () => 'password_or_username_invalid_error'
  )
  .on(
    api.events.logic.countryNotValid, // WG0031
    () => 'invalid_country_error'
  )
  .on(
    api.events.logic.deviceTypeNotAllowed, // EC21 | WG0016
    () => 'web_app_disabled_error'
  )
  .on(
    api.events.logic.invalidReferer, // WG0021
    () => 'domain_disabled'
  )
  .on(
    api.events.logic.maxNumberDevicesAssigned, // EC06 | WG0006
    () => 'max_devices_assigned_error'
  )
  .on(
    api.events.logic.serverUnderMaintenance, // 503
    () => 'server_maintenance_error'
  )
  .on(
    api.events.logic.subscriptionNotActive, // WG0023
    () => 'subscription_expired_error'
  )
  .on(
    api.events.logic.unsupportedPlatform, // WG0035
    () => 'app_not_supported_error'
  )
  .on(
    api.events.logic.webDeviceInitError, // WG0020
    () => 'failed_init_app_error'
  )
  .on(
    api.events.logic.webDeviceNotFound, // EC22
    () => 'failed_open_app_error'
  )
  .on(
    api.events.logic.yourIPAddressLocked, // EC55 | WG0018
    () => 'ip_blocked_error'
  )
  .on(
    api.events.logic.internalServerError, // 500 | WB0500 | WG0500
    () => 'unknown_error'
  )
  .on(
    api.events.logic.signInByPhoneDisabled, // WG0094
    () => 'phone_number_sign_in_disabled_error'
  )
  .on(
    api.events.logic.tooManyCodeRequests, // WG0094
    () => 'code_request_attempt_error'
  )
  // TODO: It is necessary to reconsider the approach to the implementation of error translation with additional options
  .on(
    api.events.logic.noMoreVerifyAttempts, // WG0099
    (_, { payload }) => {
      const time = formatDateDuration({
        duration: {
          seconds: payload?.remainsSeconds,
        },
      })
      return t('invalid_sms_code_attempt_with_time_error', {
        time,
      })
    }
  )
  .on(
    api.events.logic.phoneNumberNotValid, // WG0027
    () => 'invalid_phone_number_error'
  )
  .on(
    artificialSubscriptionHasBeenExpired, // api.events.logic.subscriptionHasBeenExpired, // WG0009
    () => 'subscription_expired_error'
  )
  .on(
    artificialAccountOrDeviceHasBeenSuspended, // api.events.logic.accountOrDeviceHasBeenSuspended, // WG0015, WG0103
    () => 'account_or_device_suspended_error'
  )
  .on(
    artificialSubscriptionIsApprovalPending, // api.events.logic.subscriptionIsApprovalPending, // WG0104
    () => 'subscription_pending_error_title'
  )
  // some generic request errors
  .on(
    api.events.http.unauthorized, // 401
    (error, data) => {
      const isGuest401 = data?.request?.url?.includes('/guest')

      return isGuest401 ? error : 'session_expired_error'
    }
  )
  .on(
    api.events.http.httpError, // any non-200 error
    (error, { request, response }) => {
      const isGuest401 =
        request?.url?.includes('/guest') && response?.status === 401

      if (isGuest401 || error !== null) return error

      return 'unknown_error'
    }
  )
  .on(
    api.events.http.error, // any request error (dns, carrier, etc.)
    (error) => (error === null ? 'request_failed_error' : error)
  )

debounce({
  source: $error,
  timeout: 50,
  target: onError,
})
sample({
  source: onError,
  filter: (error) => aye(error),
  target: signInModel.$viewError,
})

sample({
  clock: signInModel.$signInWithPhoneEnabled,
  fn: (phoneEnabled) => (phoneEnabled ? AuthTab.Phone : AuthTab.Email),
  target: tabsModel.$selectedTab,
})

sample({
  clock: updateTextError,
  fn: (data) => data.translated,
  target: signInModel.$viewError,
})

sample({
  clock: [userUpdatesFormAfterGrace, initNot401, init],
  target: stopTimerError,
})

signInModel.$viewError.reset(
  userUpdatesFormAfterGrace,
  initNot401,
  signInModel.signIn
)

signInByTrueIdFeatureModel.$trueIdError.reset(left)
tabsModel.$selectedTab.reset(init)
